import { customAlphabet } from "nanoid";
import { WhatsAppContactType, ServiceType, SendContentType } from "../..";
import {
  WhatsAppSendFileParams,
  WhatsAppSendImageParams,
  WhatsAppSendTextParams,
} from "../../services/whatsapp-command";
import Command from "../../services";

const urlAlphabet =
  "useandom-26T198340PX75pxJACKVERYMINDBUSHWOLFGQZbfghjklqvwyzrict";
/** 自定义nanoid字符串集，去掉默认字符串集合中的_ */
const nanoid = customAlphabet(urlAlphabet);

export type WhatsAppTextSendData = {
  content: string;
};

type UrlBaseSendData = {
  path: string;
  file_name: string;
};

export type WhatsAppImageSendData = UrlBaseSendData;
export type WhatsAppFileSendData = UrlBaseSendData;

export type WhatsAppSendDataType = {
  type: SendContentType;
  send_data:
    | WhatsAppTextSendData
    | WhatsAppImageSendData
    | WhatsAppFileSendData;
};

type BaseSendParams =
  | WhatsAppSendTextParams
  | WhatsAppSendImageParams
  | WhatsAppSendFileParams;

class BaseSend<T extends WhatsAppSendDataType, K extends BaseSendParams> {
  constructor(
    /** 发送消息的指令方法 */
    public sendMethod: (
      from_user_id: string,
      service_type: ServiceType,
      data: K
    ) => void,
    /** 实例化 BaseSend 时传进来的 send_params，存放到 db，用于失败重发 */
    public sendMessageParams: SendWhatsAppMessageParams<T>
  ) {}
  async emit(params: {
    data: T;
    from_user_id: string;
    service_type: ServiceType;
    chat_id: string;
    to_id: string;
    chat_type: WhatsAppContactType;
  }): Promise<Omit<SendWhatsAppMessageParams<T>, "service_type">> {
    const request_key = nanoid();
    const { data, from_user_id, service_type, chat_id, to_id, chat_type } =
      params;
    const { send_data } = data;
    const sendParams = {
      ...send_data,
      chat_id,
      to_id,
      chat_type,
      request_key: this.sendMessageParams?.request_key || request_key,
    } as K;
    this.sendMethod!(from_user_id, service_type, sendParams);
    this.sendMessageParams = {
      ...this.sendMessageParams,
      request_key,
    };
    Reflect.deleteProperty(this.sendMessageParams, "service_type");
    return Promise.resolve(this.sendMessageParams);
  }
}

export type SendWhatsAppMessageParams<T> = {
  //  输入框的数据
  data: T;
  from_user_id: string;
  service_type: ServiceType;
  chat_id: string;
  to_id: string;
  chat_type: WhatsAppContactType;
  request_key?: string;
};

export const sendWhatsAppMessage = async (
  params: SendWhatsAppMessageParams<WhatsAppSendDataType>
): Promise<
  Omit<SendWhatsAppMessageParams<WhatsAppSendDataType>, "service_type">
> => {
  const { data, from_user_id, service_type, chat_id, to_id, chat_type } =
    params;
  const { type } = data;
  switch (type) {
    case SendContentType.TEXT: {
      const sendText = new BaseSend(
        Command.WhatsAppCommand.sendTextCommand,
        params
      );
      return await sendText.emit({
        data,
        from_user_id,
        service_type,
        chat_id,
        to_id,
        chat_type,
      });
    }
    case SendContentType.IMAGE: {
      const sendImage = new BaseSend(
        Command.WhatsAppCommand.sendImageCommand,
        params
      );
      return await sendImage.emit({
        data,
        from_user_id,
        service_type,
        chat_id,
        to_id,
        chat_type,
      });
    }
    case SendContentType.FILE: {
      const sendFile = new BaseSend(
        Command.WhatsAppCommand.sendFileCommand,
        params
      );
      return await sendFile.emit({
        data,
        from_user_id,
        service_type,
        chat_id,
        to_id,
        chat_type,
      });
    }
  }
};
